/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import buildThreadTimeline from '@/lib/assistant/buildThreadTimeline.js';
import resolveThreadExecution from '@/lib/assistant/resolveThreadExecution.js';
import sortThreadItemsByActivity from '@/lib/assistant/sortThreadItemsByActivity.js';
import { CONVERSATION_THREAD_TYPES } from '@/lib/threads/threadTypeValues.js';
import { EMPTY_ARRAY } from '@/redux/selectors/pieveSelectors/constants.js';
import { threadItems } from '@/redux/selectors/pieveSelectors/sidebarThreadCollectionSelectors.js';
import { threadPendingInteractions, threadVisibleActivity } from '@/redux/selectors/pieveSelectors/threadActivitySelectors.js';
import { resolvedThreadBlockerMessage } from '@/redux/selectors/pieveSelectors/threadBlockerSelectors.js';
import { isThreadRunning, threadRunState } from '@/redux/selectors/pieveSelectors/threadRunSelectors.js';

const ASSISTANT_THREAD_TYPES = new Set(CONVERSATION_THREAD_TYPES);

/** The conversations listed by the assistant, newest activity first: requests, onboarding and the conversations that build automations (automation runs live in Automations). */
export const assistantThreadItems = createSelector([threadItems], (items) => {
  return sortThreadItemsByActivity(items.filter((item) => ASSISTANT_THREAD_TYPES.has(item.threadType)));
});

export const archivedThreads = (state) => state?.pieve?.archivedThreads || EMPTY_ARRAY;

export const threadSearchResults = (state) => state?.pieve?.sidebarSearchThreads || EMPTY_ARRAY;

/** What the thread view draws for one thread: requests, replies, step trails, approvals and notices. */
export const threadTimeline = createSelector([threadVisibleActivity, isThreadRunning], (activity, isRunInProgress) => {
  return buildThreadTimeline({ activity, isRunInProgress });
});

/** Where the thread's latest run stands: one execution state, what Pieve is doing and what comes next. */
export const threadExecution = createSelector([threadRunState, threadPendingInteractions, threadTimeline, resolvedThreadBlockerMessage], (runState, pendingInteractions, timeline, blockerMessage) => {
  const lastSteps = [...timeline].reverse().find((block) => block.kind === 'steps');
  const runningStep = lastSteps?.steps.find((step) => step.state === 'running');

  return resolveThreadExecution({
    runState,
    pendingCount: pendingInteractions.length,
    currentStep: runningStep?.label || '',
    blockerMessage,
    hasActivity: timeline.length > 0
  });
});
